import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  MagnifyingGlassIcon, 
  PlusIcon, 
  ChevronRightIcon,
  FunnelIcon,
  ChatBubbleBottomCenterTextIcon
} from '@heroicons/react/24/outline';
import { customerApi } from '../../api/customerApi';

const Customers = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // all, get, give
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await customerApi.getCustomers();
      setCustomers(response.data.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.phone.includes(searchTerm);
    if (!matchesSearch) return false;
    
    if (filter === 'get') return c.currentBalance > 0;
    if (filter === 'give') return c.currentBalance < 0;
    return true; // all
  });

  const handleWhatsAppReminder = async (e, customer) => {
    e.stopPropagation();
    try {
      await customerApi.sendReminder(customer._id);
      
      const dueAmount = Math.abs(customer.currentBalance);
      const paymentLink = `http://localhost:5173/pay/mock_${Math.random().toString(36).substring(7)}`;
      const message = `Hello ${customer.name},\nThis is a gentle reminder regarding your pending balance of Rs. ${dueAmount}.\nPlease process the payment via this link: ${paymentLink}\n\nThank you!`;
      const waUrl = `https://wa.me/91${customer.phone}?text=${encodeURIComponent(message)}`;
      
      window.open(waUrl, '_blank');
      fetchCustomers(); // Refresh to update lastRemindedAt
    } catch (error) {
      console.error('Failed to send reminder', error);
      alert('Could not log reminder');
    }
  };

  return (
    <div className="w-full pb-10 max-w-7xl mx-auto space-y-6">
      
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">Your Customers</h1>
          <p className="text-[14px] text-gray-500 dark:text-gray-400 mt-1">Manage all your customer ledgers and balances.</p>
        </div>
        <Link 
          to="/customers/add" 
          className="flex items-center gap-2 bg-[#dc343b] text-white px-5 py-2.5 rounded-xl font-bold text-[13px] hover:bg-[#b91c1c] transition-colors shadow-sm shadow-red-500/20"
        >
          <PlusIcon className="h-4 w-4" />
          Add New Customer
        </Link>
      </div>

      {/* Toolbar: Search & Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-100 dark:border-gray-700 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] flex flex-col md:flex-row gap-4 justify-between transition-colors">
        
        {/* Search */}
        <div className="relative w-full md:max-w-md">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>
          <input
            type="text"
            className="block w-full rounded-xl border-0 py-3 pl-11 pr-4 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700/50 ring-1 ring-inset ring-gray-100 dark:ring-gray-700 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#dc343b] text-[14px] transition-all"
            placeholder="Search by name or phone number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 scrollbar-hide">
          <div className="flex items-center gap-2 px-3 border-r border-gray-100 dark:border-gray-700">
            <FunnelIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            <span className="text-[13px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Filter</span>
          </div>
          <button 
            onClick={() => setFilter('all')}
            className={`whitespace-nowrap px-4 py-2 rounded-lg text-[13px] font-bold transition-colors ${filter === 'all' ? 'bg-gray-900 dark:bg-gray-700 text-white' : 'bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
          >
            All Customers
          </button>
          <button 
            onClick={() => setFilter('get')}
            className={`whitespace-nowrap px-4 py-2 rounded-lg text-[13px] font-bold transition-colors ${filter === 'get' ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400' : 'bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400 hover:bg-green-50 dark:hover:bg-green-900/20 hover:text-green-600 dark:hover:text-green-400'}`}
          >
            You Will Get
          </button>
          <button 
            onClick={() => setFilter('give')}
            className={`whitespace-nowrap px-4 py-2 rounded-lg text-[13px] font-bold transition-colors ${filter === 'give' ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400' : 'bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400'}`}
          >
            You Will Give
          </button>
        </div>
      </div>

      {/* Customer List */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] overflow-hidden transition-colors">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#dc343b] mb-4"></div>
            <p className="text-[14px] font-medium text-gray-500 dark:text-gray-400">Loading customers...</p>
          </div>
        ) : filteredCustomers.length > 0 ? (
          <div className="divide-y divide-gray-50 dark:divide-gray-700/50">
            {filteredCustomers.map((customer) => (
              <div 
                key={customer._id}
                onClick={() => navigate(`/customers/${customer._id}/ledger`)}
                className="group flex items-center justify-between p-4 sm:p-5 hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center text-gray-700 dark:text-gray-300 font-bold text-[16px] shadow-sm">
                    {customer.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h4 className="text-[15px] font-bold text-gray-900 dark:text-white group-hover:text-[#dc343b] dark:group-hover:text-red-400 transition-colors">{customer.name}</h4>
                    <p className="text-[12px] font-medium text-gray-500 dark:text-gray-400 mt-0.5">{customer.phone}</p>
                  </div>
                </div>
                
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">
                        {customer.currentBalance > 0 ? "You Will Get" : customer.currentBalance < 0 ? "You Will Give" : "Settled"}
                      </p>
                      <p className={`text-[16px] font-extrabold ${customer.currentBalance > 0 ? 'text-green-600 dark:text-green-500' : customer.currentBalance < 0 ? 'text-red-600 dark:text-red-500' : 'text-gray-400'}`}>
                        ₹{Math.abs(customer.currentBalance || 0).toLocaleString()}
                      </p>
                      {customer.lastRemindedAt && (
                        <p className="text-[10px] text-gray-400 mt-0.5">
                          Reminded: {new Date(customer.lastRemindedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    {customer.currentBalance > 0 && (
                      <button 
                        onClick={(e) => handleWhatsAppReminder(e, customer)}
                        className="hidden sm:flex h-8 w-8 rounded-full items-center justify-center bg-green-50 dark:bg-green-900/30 text-green-600 border border-green-200 dark:border-green-800 hover:bg-green-500 hover:text-white transition-all shadow-sm"
                        title="Send WhatsApp Reminder"
                      >
                        <ChatBubbleBottomCenterTextIcon className="h-4 w-4" />
                      </button>
                    )}
                    <div className="hidden sm:flex h-8 w-8 rounded-full items-center justify-center bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-400 group-hover:border-[#dc343b] dark:group-hover:border-red-400 group-hover:text-[#dc343b] dark:group-hover:text-red-400 group-hover:shadow-sm transition-all">
                      <ChevronRightIcon className="h-4 w-4" />
                    </div>
                  </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <MagnifyingGlassIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-[16px] font-bold text-gray-900 dark:text-white mb-1">No customers found</h3>
            <p className="text-[14px] text-gray-500 dark:text-gray-400">
              {searchTerm ? `We couldn't find anyone matching "${searchTerm}".` : "You haven't added any customers yet."}
            </p>
            {!searchTerm && (
              <button 
                onClick={() => navigate('/customers/add')}
                className="mt-6 text-[13px] font-bold text-[#dc343b] hover:text-[#b91c1c] uppercase tracking-widest"
              >
                + Add Your First Customer
              </button>
            )}
          </div>
        )}
      </div>

    </div>
  );
};

export default Customers;
